import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  CheckCircle,
  Circle,
  ArrowLeft,
  PencilSimple,
  Info,
  Barbell,
  Heart,
  ArrowRight,
  Download,
  Flame,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import ExerciseInfoModal from "../components/builder/ExerciseInfoModal";
import { DAY_LABELS } from "../hooks/usePlanBuilder";
import { exportPlanToPDF } from "../lib/pdf";

/** Read-only workout log page. Focus: log completions and follow the plan
 *  without the clutter of drag handles, sets/reps inputs, and structural
 *  editors. Click "Edit Plan" to jump into /builder/:id. */
export default function WorkoutLog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [exMap, setExMap] = useState({});
  const [weekIdx, setWeekIdx] = useState(0);
  const [infoModal, setInfoModal] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) { navigate("/dashboard", { replace: true }); return; }
    api.get(`/plans/${id}`).then((r) => setPlan(r.data)).catch(() => navigate("/dashboard", { replace: true }));
    api.get("/exercises").then((r) => {
      const m = {};
      (r.data || []).forEach((e) => { m[e.id] = e; });
      setExMap(m);
    });
  }, [id, navigate]);

  const { weeks = [], completions = {} } = plan || {};
  const week = weeks[weekIdx];

  const stats = useMemo(() => {
    let total = 0, done = 0;
    weeks.forEach((w) => {
      (w.days || []).forEach((d) => {
        if (!d.exercises?.length) return;
        total++;
        if (completions[`${w.week_index}-${d.day}`]) done++;
      });
    });
    return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
  }, [weeks, completions]);

  const toggleDay = async (wi, day) => {
    if (!plan || busy) return;
    const key = `${wi}-${day}`;
    const completed = !completions[key];
    setBusy(true);
    try {
      const { data } = await api.post(`/plans/${plan.id}/complete`, { week_index: wi, day, completed });
      // Defensive: if backend ever returns just {completions}, keep existing
      // plan fields so the page doesn't blank out.
      setPlan((prev) => (data && data.weeks ? data : { ...prev, ...data }));
    } catch (e) {
      alert("Failed to update: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const progressWeek = async (weekType) => {
    if (!plan || !week) return;
    if (!window.confirm(
      weekType === "progress" ? `Spin up Week ${week.week_index + 1} with +5% weight / +2 reps on every exercise you completed this week?`
      : weekType === "deload" ? `Create a deload Week ${week.week_index + 1} (-10% weights, reduced volume)?`
      : weekType === "peak" ? `Create a peak Week ${week.week_index + 1} (+5% weight, heavy triples)?`
      : `Create Week ${week.week_index + 1} as a verbatim repeat of this week?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/plans/${plan.id}/progress-week`, {
        from_week_index: week.week_index,
        week_type: weekType,
      });
      setPlan(data);
      setWeekIdx(data.weeks.length - 1);
    } catch (e) {
      alert("Failed to add week: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  if (!plan) return <Layout><div className="label-mono animate-pulse">LOADING MISSION...</div></Layout>;

  return (
    <Layout>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-volt text-xs uppercase tracking-widest inline-flex items-center gap-1 mb-2"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={12} weight="bold" /> Dashboard
          </button>
          <h1 className="font-display text-4xl sm:text-5xl font-black leading-tight truncate">{plan.name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs label-mono text-muted-foreground">
            {plan.type && <span>{plan.type.toUpperCase()}</span>}
            {plan.level && <span>· LEVEL: {plan.level.toUpperCase()}</span>}
            {plan.goal && <span>· GOAL: {plan.goal.toUpperCase()}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/builder/${plan.id}`}
            className="inline-flex items-center gap-2 border border-border px-4 py-2 hover:border-volt hover:text-volt text-xs uppercase tracking-widest"
            data-testid="edit-plan-btn"
          >
            <PencilSimple size={14} weight="bold" /> Edit Plan
          </Link>
          <button
            onClick={() => exportPlanToPDF(plan, exMap)}
            className="inline-flex items-center gap-2 border border-border px-4 py-2 hover:border-volt hover:text-volt text-xs uppercase tracking-widest"
            data-testid="export-pdf-btn"
          >
            <Download size={14} weight="bold" /> PDF
          </button>
        </div>
      </div>

      {/* Progress strip */}
      <div className="tactical-card p-5 mb-6" data-testid="progress-summary">
        <div className="flex items-end justify-between mb-2">
          <div>
            <div className="label-mono text-muted-foreground">// OVERALL PROGRESS</div>
            <div className="font-display text-4xl font-black leading-none mt-1">
              {stats.done}<span className="text-muted-foreground text-xl">/{stats.total}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">workouts completed</div>
          </div>
          <div className="text-right">
            <div className="font-display text-5xl font-black text-volt leading-none">{stats.pct}<span className="text-2xl">%</span></div>
            <div className="text-xs text-muted-foreground mt-1">cycle complete</div>
          </div>
        </div>
        <div className="h-2 bg-secondary overflow-hidden mt-4">
          <div className="h-full bg-volt transition-all" style={{ width: `${stats.pct}%` }} />
        </div>
      </div>

      {/* Week navigator */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="week-nav">
        {weeks.map((w, i) => {
          const weekStats = (w.days || []).reduce((acc, d) => {
            if (!d.exercises?.length) return acc;
            acc.total++;
            if (completions[`${w.week_index}-${d.day}`]) acc.done++;
            return acc;
          }, { total: 0, done: 0 });
          const isActive = i === weekIdx;
          return (
            <button
              key={w.week_index}
              onClick={() => setWeekIdx(i)}
              className={`border px-4 py-2 text-left transition-colors ${
                isActive ? "border-volt bg-volt/10 text-volt" : "border-border hover:border-volt/50"
              }`}
              data-testid={`week-tab-${w.week_index}`}
            >
              <div className="font-display font-bold uppercase text-sm tracking-widest">Week {w.week_index}</div>
              <div className="text-[10px] text-muted-foreground">{weekStats.done}/{weekStats.total} done</div>
            </button>
          );
        })}
      </div>

      {/* Current week intent */}
      {week && (
        <>
          {week.intent && (
            <div className="mb-4 border-l-2 border-volt pl-3 py-1 text-sm text-muted-foreground italic">
              {week.intent}
            </div>
          )}

          {/* Days grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="days-grid">
            {week.days.map((d) => (
              <DayLogCard
                key={d.day}
                weekIdx={week.week_index}
                day={d}
                completed={!!completions[`${week.week_index}-${d.day}`]}
                exMap={exMap}
                onToggle={() => toggleDay(week.week_index, d.day)}
                onInfo={(exId) => setInfoModal({ exId })}
                busy={busy}
                planId={plan.id}
              />
            ))}
          </div>

          {/* Add next week row (only on last week) */}
          {weekIdx === weeks.length - 1 && (
            <div className="mt-8 border-2 border-dashed border-border p-4" data-testid="progress-week-row">
              <div className="label-mono text-muted-foreground mb-3">// READY FOR WEEK {week.week_index + 1}?</div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <button onClick={() => progressWeek("progress")} disabled={busy} className="border border-volt text-volt px-3 py-3 hover:bg-volt hover:text-black transition-colors disabled:opacity-40" data-testid="log-progress-week">
                  <div className="font-display font-bold uppercase tracking-widest text-sm">Progress ↑</div>
                  <div className="text-xs opacity-80 mt-1">+5% / +2 reps</div>
                </button>
                <button onClick={() => progressWeek("deload")} disabled={busy} className="border border-border px-3 py-3 hover:border-volt transition-colors disabled:opacity-40" data-testid="log-deload-week">
                  <div className="font-display font-bold uppercase tracking-widest text-sm">Deload ↓</div>
                  <div className="text-xs text-muted-foreground mt-1">-10%, plyo cut</div>
                </button>
                <button onClick={() => progressWeek("peak")} disabled={busy} className="border border-border px-3 py-3 hover:border-volt transition-colors disabled:opacity-40" data-testid="log-peak-week">
                  <div className="font-display font-bold uppercase tracking-widest text-sm">Peak ↑↑</div>
                  <div className="text-xs text-muted-foreground mt-1">Heavy triples</div>
                </button>
                <button onClick={() => progressWeek("repeat")} disabled={busy} className="border border-border px-3 py-3 hover:border-volt transition-colors disabled:opacity-40" data-testid="log-repeat-week">
                  <div className="font-display font-bold uppercase tracking-widest text-sm">Repeat</div>
                  <div className="text-xs text-muted-foreground mt-1">Same as this week</div>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {infoModal && (
        <ExerciseInfoModal
          exercise={exMap[infoModal.exId]}
          exMap={exMap}
          onClose={() => setInfoModal(null)}
        />
      )}
    </Layout>
  );
}

function DayLogCard({ weekIdx, day, completed, exMap, onToggle, onInfo, busy, planId }) {
  const navigate = useNavigate();
  const label = day.label || DAY_LABELS[day.day - 1];
  const isCarried = (day.label || "").includes("CARRIED");
  const focusLabel = day.focus || "Training";
  const isRecovery = /mobility|recovery|rest/i.test(focusLabel);
  const isEndurance = /endurance|aerobic|anaerobic/i.test(focusLabel);
  const FocusIcon = isRecovery ? Heart : isEndurance ? ArrowRight : Barbell;
  const empty = !day.exercises || day.exercises.length === 0;

  return (
    <div className={`border ${completed ? "border-volt bg-volt/5" : "border-border bg-card"} p-4 transition-colors`} data-testid={`day-card-${weekIdx}-${day.day}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 label-mono text-muted-foreground">
            <FocusIcon size={12} weight="bold" />
            <span>{focusLabel.toUpperCase()}</span>
            {isCarried && <span className="text-amber-500">· CARRIED</span>}
          </div>
          <div className="font-display text-lg font-bold uppercase mt-0.5 truncate">{label}</div>
        </div>
        {!empty && (
          <div className="flex flex-col gap-1 shrink-0 items-end">
            <button
              onClick={() => navigate(`/session/${planId}/${weekIdx}/${day.day}`)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-volt text-black text-[10px] uppercase tracking-widest font-bold hover:opacity-90"
              data-testid={`start-session-${weekIdx}-${day.day}`}
            >
              <Flame size={12} weight="fill" /> Start
            </button>
            <button
              onClick={onToggle}
              disabled={busy}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border text-[10px] uppercase tracking-widest transition-colors ${
                completed ? "border-volt text-volt bg-volt/10 hover:bg-volt hover:text-black" : "border-border hover:border-volt hover:text-volt"
              } disabled:opacity-50`}
              data-testid={`toggle-complete-${weekIdx}-${day.day}`}
            >
              {completed ? <CheckCircle size={12} weight="fill" /> : <Circle size={12} weight="bold" />}
              {completed ? "Done" : "Quick Done"}
            </button>
          </div>
        )}
      </div>

      {empty ? (
        <div className="text-xs text-muted-foreground italic py-4">Rest day — no prescribed work.</div>
      ) : (
        <ExerciseListGrouped exercises={day.exercises} exMap={exMap} onInfo={onInfo} weekIdx={weekIdx} dayNum={day.day} />
      )}
    </div>
  );
}

// Section meta — kept local so we don't churn imports across the page.
const SECTION_META = {
  warmup: { label: "WARM-UP", color: "text-orange-400" },
  workout: { label: "WORKOUT", color: "text-volt" },
  cooldown: { label: "COOL-DOWN", color: "text-sky-400" },
};
const SECTION_ORDER = ["warmup", "workout", "cooldown"];

function ExerciseListGrouped({ exercises, exMap, onInfo, weekIdx, dayNum }) {
  // Bucket by section so the operator sees their warm-up / workout / cool-down
  // pre-split before clicking Start. Exercises without a section default to
  // "workout".
  const buckets = { warmup: [], workout: [], cooldown: [] };
  exercises.forEach((ex, i) => {
    const key = buckets[ex.section] ? ex.section : "workout";
    buckets[key].push({ ex, originalIdx: i });
  });
  const present = SECTION_ORDER.filter((s) => buckets[s].length > 0);

  // If only one bucket has items, skip the section headers — keeps the
  // existing visual identical for legacy plans without sections assigned.
  if (present.length === 1) {
    return (
      <div className="space-y-1.5">
        {buckets[present[0]].map(({ ex, originalIdx: i }) => (
          <ExerciseRow key={`${ex.exercise_id}-${i}`} ex={ex} idx={i} exMap={exMap} onInfo={onInfo} weekIdx={weekIdx} dayNum={dayNum} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {present.map((s) => (
        <div key={s} data-testid={`log-section-${s}-${weekIdx}-${dayNum}`}>
          <div className={`label-mono text-[10px] mb-1 ${SECTION_META[s].color}`}>// {SECTION_META[s].label}</div>
          <div className="space-y-1.5">
            {buckets[s].map(({ ex, originalIdx: i }) => (
              <ExerciseRow key={`${ex.exercise_id}-${i}`} ex={ex} idx={i} exMap={exMap} onInfo={onInfo} weekIdx={weekIdx} dayNum={dayNum} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ExerciseRow({ ex, idx: i, exMap, onInfo, weekIdx, dayNum }) {
  const def = exMap[ex.exercise_id];
  return (
    <div className="flex items-center gap-2 text-sm py-1.5 border-b border-border/50 last:border-b-0" data-testid={`log-ex-${weekIdx}-${dayNum}-${i}`}>
      <button
        onClick={() => onInfo(ex.exercise_id)}
        className="text-muted-foreground hover:text-volt shrink-0"
        title="View instructions"
        data-testid={`log-ex-info-${weekIdx}-${dayNum}-${i}`}
      >
        <Info size={14} weight="bold" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold uppercase text-xs tracking-wide truncate">{def?.name || ex.exercise_id}</div>
        {ex.notes && <div className="text-[10px] text-volt truncate">{ex.notes}</div>}
      </div>
      <div className="shrink-0 text-xs font-mono text-muted-foreground text-right">
        {formatPrescription(ex)}
      </div>
    </div>
  );
}

function formatPrescription(ex) {
  const bits = [];
  if (ex.sets) bits.push(`${ex.sets}×`);
  if (ex.reps) bits.push(`${ex.reps}`);
  let line1 = bits.join("");
  const out = [];
  if (line1) out.push(line1);
  if (ex.weight) out.push(ex.weight);
  if (ex.rpe) out.push(`RPE ${ex.rpe}`);
  return out.length ? out.join(" @ ") : "—";
}
